/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import { styled, alpha, useTheme } from "@mui/material/styles";
import { Box } from "@mui/material";
import { ResultJobList } from "client";
// import { useMapDataContext } from "context/MapDataProvider";
import { FC, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { Const } from "const";
import { useUserContext } from "context/UserContext";
import { getNodeType } from "components/Map/NodeMapOverlay";

export type JobCardStatus =
  | "hidden"
  | "undelivered"
  | "delivered"
  | "running"
  | "completed"
  | "canceled"
  | "failed"
  | "unapproved";

/**
 * Determine task card display status
 *
 * @param {?number} [taskStatus] Task status
 * @param {?Date} [endDatetime] End period
 * @return {JobCardStatus} task card display status
 */
const determineCardStatus = (
  taskStatus?: number,
  endDatetime?: Date
): JobCardStatus => {
  let status: JobCardStatus = "hidden";
  switch (taskStatus) {
    case 1:
      status = endDatetime ? "delivered" : "undelivered";
      break;
    case 2:
      status = "running";
      break;
    case 3:
      status = "completed";
      break;
    case 4:
    case 5:
      status = "canceled";
      break;
  }
  return status;
};

export const getJobCardStatus = (
  jobSetStatus?: number,
  taskStatus?: number,
  endDatetime?: Date,
  taskKind?: number,
  roleName?: string
): JobCardStatus => {
  let status: JobCardStatus = "hidden";
  switch (jobSetStatus) {
    case 1:
    case 2:
      // not display
      break;
    case undefined:
      if (taskKind === 4) {
        // For go home task
        // Determine card status
        status = determineCardStatus(taskStatus, endDatetime);
      }
      break;
    case 3:
      // Determine card status
      status = determineCardStatus(taskStatus, endDatetime);
      break;
    case 4:
    case 5:
      status = "failed";
      break;
    case 6:
      if (taskStatus === 6 && roleName !== "awv_operator") {
        status = "unapproved";
      }
      break;
  }
  return status;
};

export const JobCard: FC<ResultJobList> = ({
  jobSetStatus,
  taskStatus,
  taskKind,
  startDatetime,
  endDatetime,
  inputDate,
  updateDate,
  planTaskExecutionTime,
  nodeCategory,
  nodeName,
  materialName,
  chargerName
}) => {
  const { t } = useTranslation();
  // const { mapData } = useMapDataContext();
  const theme = useTheme();
  const { roleName } = useUserContext();
  const [cardColor, setCardColor] = useState(Const.taskCard.cardWhiteColor);
  const [frameColor, setFrameColor] = useState(theme.palette.error.main);
  const [progress, setProgress] = useState(0);
  const [unapproved, setUnapproved] = useState(false);
  const [edited, setEdited] = useState(false);
  const [emphasizeClass, setEmphasizeClass] = useState<string>();
  const [isFailed, setIsFailed] = useState(false);

  const isCargeJob = nodeCategory === 6 ? true : false

  useEffect(() => {
    // set colors by state, judge (un)approved
    switch (
      getJobCardStatus(
        jobSetStatus,
        taskStatus,
        endDatetime,
        taskKind,
        roleName
      )
    ) {
      case "undelivered":
        setCardColor(Const.taskCard.cardWhiteColor);
        setFrameColor(Const.taskCard.frameGreyColor);
        setUnapproved(false);
        setIsFailed(false);
        break;
      case "delivered":
        setCardColor(Const.taskCard.cardWhiteColor);
        if (isCargeJob) {
          setFrameColor(Const.taskCard.frameOrangeColor)
        } else {
          setFrameColor(Const.taskCard.frameGreenColor);
        }
        setUnapproved(false);
        setIsFailed(false);
        break;
      case "running":
        setCardColor(Const.taskCard.cardGreenColor);
        if (isCargeJob) {
          setFrameColor(Const.taskCard.frameOrangeColor)
        } else {
          setFrameColor(Const.taskCard.frameGreenColor);
        }
        setUnapproved(false);
        setIsFailed(false);
        break;
      case "completed":
        setCardColor(Const.taskCard.cardGreyColor);
        setFrameColor(Const.taskCard.frameGreyColor);
        setUnapproved(false);
        setIsFailed(false);
        break;
      case "canceled":
        setCardColor(Const.taskCard.cardGreyColor);
        setFrameColor(Const.taskCard.frameGreyColor);
        // setArrivalTime("--:--");
        setUnapproved(false);
        setIsFailed(false);
        // arrivalTime is set, so return.
        return;
      case "failed":
        setCardColor(Const.taskCard.cardWhiteColor);
        setFrameColor(theme.palette.error.main);
        setUnapproved(false);
        setIsFailed(true);
        break;
      case "unapproved":
        setCardColor(Const.taskCard.cardWhiteColor);
        setFrameColor(Const.taskCard.frameGreyColor);
        setUnapproved(true);
        setIsFailed(false);
        break;
    }
  }, [jobSetStatus, taskStatus, endDatetime, taskKind, theme, roleName, isCargeJob]);

  useEffect(() => {
    let period = Math.max(60, planTaskExecutionTime ?? 60);
    const st = moment(startDatetime);
    if (st.date() !== st.clone().add(period, "minutes").date()) {
      // When multi day
      period = st.clone().endOf("day").diff(st, "minutes");
    }
    setProgress(Math.min(1, (planTaskExecutionTime ?? 60) / period) * 100);
  }, [planTaskExecutionTime, startDatetime]);

  useEffect(() => {
    const inputMomentDate = inputDate ? moment(inputDate) : moment.invalid();
    const updateMomentDate = updateDate ? moment(updateDate) : moment.invalid();
    // judge edited
    if (inputMomentDate.isValid() && updateMomentDate.isValid()) {
      setEdited(inputMomentDate.isBefore(updateMomentDate));
    } else {
      setEdited(false);
    }
    // judge just registered
    const diff = updateMomentDate.isValid()
      ? moment().diff(updateMomentDate)
      : Number.MAX_SAFE_INTEGER;
    if (diff <= 13000) {
      setEmphasizeClass((pre) => {
        if (pre !== "justRegistered") {
          setTimeout(
            () => setEmphasizeClass(undefined),
            Math.max(3000, 13000 - diff)
          );
        }
        return "justRegistered";
      });
    }
  }, [inputDate, updateDate]);

  const cardStatus = getJobCardStatus(
    jobSetStatus,
    taskStatus,
    endDatetime,
    taskKind,
    roleName
  );
  if (cardStatus === "hidden") {
    // do not displayed.
    return <></>;
  }

  let statusText = "";
  if (unapproved) {
    statusText = t("AWVUI-MSG157");
  } else if (edited) {
    statusText = t("AWVUI-MSG79");
  }
  const failed = isFailed ? t("AWVUI-MSG80") : "";

  let labelByNodeCategory = "";
  if (isCargeJob) {
    labelByNodeCategory = t("AWVUI-MSG195")
  } else if (nodeCategory === 1) {
    labelByNodeCategory = t("AWVUI-MSG20")
  } else if (nodeCategory === 2) {
    labelByNodeCategory = t("AWVUI-MSG192")
  } else if (nodeCategory === 3) {
    labelByNodeCategory = t("AWVUI-MSG193")
  } else if (nodeCategory === 4) {
    labelByNodeCategory = t("AWVUI-MSG194")
  }

  return (
    <JobCardContainer color={cardColor}>
      <TaskFrame progress={progress} color={frameColor} />
      <RegisteredMask className={emphasizeClass}></RegisteredMask>
      <RegisteredFrame className={emphasizeClass} progress={progress} />
      <JobCardContent>
        <TaskHeader>
        <TaskTitle>{failed + ( labelByNodeCategory || "--")}</TaskTitle>
          <TaskStatus>{statusText}</TaskStatus>
        </TaskHeader>
        <TaskAttr>{getNodeType(nodeName)["label"]}</TaskAttr>
        {!isCargeJob && (
          <TaskAttr>{materialName}</TaskAttr>
        )}
        {isCargeJob && (
          <TaskAttr>{chargerName}</TaskAttr>
        )}
      </JobCardContent>
    </JobCardContainer>
  );
};

interface JobCardContainerProps {
  color: string;
}

const JobCardContainer = styled(Box)<JobCardContainerProps>(
  ({ theme, color }) => ({
    position: "relative",
    display: "flex",
    flexDirection: "row",
    height: "100%",
    backgroundColor: alpha(color, 0.8),
    borderTopRightRadius: 5,
    borderBottomRightRadius: 5,
    boxShadow: theme.shadows[3],
  })
);

interface TaskFrameProps {
  progress: number;
  color?: string;
}

const TaskFrame = styled(Box)<TaskFrameProps>(({ progress, color }) => ({
  position: "absolute",
  left: -8,
  top: 0,
  width: 8,
  height: `${progress}%`,
  backgroundColor: color ?? "transparent",
  opacity: 0.8,
  borderTopLeftRadius: 5,
  borderBottomLeftRadius: 5,
  boxShadow: "-1px 1px 2px 1px rgba(0,0,0,0.12)",
}));

const JobCardContent = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  height: "100%",
  padding: theme.spacing(0.5),
  color: theme.palette.text.primary,
  overflow: "hidden",
}));

const TaskHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "flex-start",
}));

const TaskTitle = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  flexShrink: 1,
  fontSize: theme.typography.body1.fontSize,
  fontWeight: "bold",
  lineHeight: 1.2,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
}));

const TaskStatus = styled(Box)(({ theme }) => ({
  flexShrink: 0,
  padding: theme.spacing(0.25, 0),
  color: theme.palette.text.secondary,
  fontSize: theme.typography.caption.fontSize,
  fontWeight: "bold",
  "&:empty": {
    display: "none",
  },
}));

const TaskAttr = styled(Box)(({ theme }) => ({
  flexShrink: 0,
  fontSize: theme.typography.body2.fontSize,
  fontWeight: 500,
  lineHeight: 1.1,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
}));

const RegisteredMask = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "transparent",
  borderWidth: 2,
  borderStyle: "solid",
  borderColor: "transparent",
  transition: "all 0.3s ease-out",
  "&.justRegistered": {
    borderColor: alpha(Const.taskCard.registeredMaskColor, 0.5),
    backgroundColor: alpha(Const.taskCard.registeredMaskColor, 0.1),
  },
}));

const RegisteredFrame = styled(TaskFrame)({
  transition: "all 0.3s ease-out",
  "&.justRegistered": {
    backgroundColor: alpha(Const.taskCard.registeredMaskColor, 0.5),
  },
});
